import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

type CalendarPermissionPromptProps = {
  onRequestPress: () => void;
  isRequesting?: boolean;
  onStartSetup?: () => void;
};

/**
 * CalendarPermissionPrompt displays an empty state
 * prompting the user to grant calendar access
 */
export function CalendarPermissionPrompt({
  onRequestPress,
  isRequesting = false,
  onStartSetup,
}: CalendarPermissionPromptProps) {
  const colorScheme = useColorScheme();
  const accentColor = Colors[colorScheme ?? 'light'].tint;
  const textColor = Colors[colorScheme ?? 'light'].text;
  const backgroundColor = Colors[colorScheme ?? 'light'].background;

  return (
    <View style={[styles.container, { backgroundColor }]}>
      <View style={styles.content}>
        <View style={[styles.iconContainer, { backgroundColor: `${accentColor}15` }]}>
          <Ionicons name="calendar-outline" size={64} color={accentColor} />
        </View>

        <Text style={[styles.title, { color: textColor }]}>Access Your Calendar</Text>

        <Text style={[styles.subtitle, { color: textColor }]}>
          FimFam needs permission to view and manage your calendar events.
          This allows you to see all your events and create new ones with AI assistance.
        </Text>

        <TouchableOpacity
          style={[
            styles.button,
            { backgroundColor: accentColor },
            isRequesting && styles.buttonDisabled,
          ]}
          onPress={onRequestPress}
          disabled={isRequesting}
          activeOpacity={0.8}
        >
          {isRequesting ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <>
              <Ionicons name="checkmark-circle-outline" size={20} color="#fff" />
              <Text style={styles.buttonText}>Allow Calendar Access</Text>
            </>
          )}
        </TouchableOpacity>

        {onStartSetup && (
          <TouchableOpacity
            style={[styles.setupButton, { borderColor: accentColor }]}
            onPress={onStartSetup}
            activeOpacity={0.8}
          >
            <Ionicons name="settings-outline" size={20} color={accentColor} />
            <Text style={[styles.setupButtonText, { color: accentColor }]}>
              Run Calendar Setup
            </Text>
          </TouchableOpacity>
        )}

        <Text style={[styles.helperText, { color: textColor }]}>
          Tap to allow calendar permissions. Your calendar data stays on your device.
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Spacing.xxl,
  },
  content: {
    maxWidth: 400,
    width: '100%',
    alignItems: 'center',
    gap: Spacing.lg,
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60, // circular
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: Spacing.sm,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    opacity: 0.8,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.sm,
    paddingVertical: Spacing.md + 2, // 14px
    paddingHorizontal: Spacing.xxl,
    borderRadius: BorderRadius.md,
    marginTop: Spacing.sm,
    minWidth: 240,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  setupButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: BorderRadius.md,
    borderWidth: 1.5,
    gap: Spacing.sm,
    marginTop: Spacing.md,
  },
  setupButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  helperText: {
    fontSize: 13,
    textAlign: 'center',
    opacity: 0.6,
    marginTop: Spacing.xs,
  },
});
